/**
 * XO Club - Hook de Gestion du Cache Service Worker
 * ==================================================
 * Ce hook vérifie automatiquement si une nouvelle version est disponible
 * et propose à l'utilisateur de rafraîchir l'application.
 */

import { useState, useEffect, useCallback } from 'react';

export function useBuildVersion() {
  const [buildInfo, setBuildInfo] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Charger les infos de build
  useEffect(() => {
    const loadBuildInfo = async () => {
      try {
        // Ajouter un cache buster pour toujours récupérer la dernière version
        const response = await fetch(`/build-config.json?t=${Date.now()}`);
        const data = await response.json();
        setBuildInfo(data);
        
        // Stocker le build ID localement
        const storedBuildId = localStorage.getItem('xoclub_build_id');
        if (storedBuildId && storedBuildId !== data.BUILD_ID) {
          setUpdateAvailable(true);
        }
        localStorage.setItem('xoclub_build_id', data.BUILD_ID);
      } catch (error) {
        console.error('[BuildVersion] Erreur chargement config:', error);
      }
    };

    loadBuildInfo();
  }, []);

  // Vérifier les mises à jour du Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setUpdateAvailable(true);
      });

      // Écouter les messages du SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'CACHE_CLEARED') {
        }
      });
    }
  }, []);

  // Forcer le rafraîchissement
  const forceRefresh = useCallback(async () => {
    setIsChecking(true);
    
    try {
      // Envoyer un message au SW pour vider le cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      }

      // Attendre un peu que le cache soit vidé
      await new Promise(resolve => setTimeout(resolve, 500));

      // Recharger la page en forçant le bypass du cache
      window.location.reload(true);
    } catch (error) {
      console.error('[BuildVersion] Erreur refresh:', error);
      window.location.reload();
    }
  }, []);

  // Vérifier manuellement les mises à jour
  const checkForUpdates = useCallback(async () => {
    setIsChecking(true);
    
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        
        // Vérifier si une mise à jour est en attente
        if (registration.waiting) {
          setUpdateAvailable(true);
          // Activer le nouveau SW
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }

      // Vérifier aussi le build config
      const response = await fetch(`/build-config.json?t=${Date.now()}`);
      const data = await response.json();
      
      if (buildInfo && data.BUILD_ID !== buildInfo.BUILD_ID) {
        setUpdateAvailable(true);
        setBuildInfo(data);
      }
    } catch (error) {
      console.error('[BuildVersion] Erreur vérification:', error);
    } finally {
      setIsChecking(false);
    }
  }, [buildInfo]);

  return {
    buildInfo,
    updateAvailable,
    isChecking,
    forceRefresh,
    checkForUpdates
  };
}

export default useBuildVersion;
